import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
 
const ThemeContext = createContext(null);
 
export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
 
export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem('app_theme');
      return stored === 'light' || stored === 'dark' ? stored : 'dark';
    } catch {
      return 'dark';
    }
  });
 
  const isDark = theme === 'dark';
 
  const toggleTheme = useCallback(() => {
    // Add .theme-transitioning to <html> so the scoped CSS transition in
    // index.css fires only during the switch — not permanently on every element.
    // This removes the Lighthouse non-composited animation warnings on
    // color / background-color / border-color for all 706 DOM elements.
    document.documentElement.classList.add('theme-transitioning');
 
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('app_theme', next);
      return next;
    });
 
    // Remove the class after the transition completes (250ms in index.css)
    const timer = setTimeout(() => {
      document.documentElement.classList.remove('theme-transitioning');
    }, 300);
 
    return () => clearTimeout(timer);
  }, []);
 
  const setTheme = useCallback((t) => {
    if (t === 'light' || t === 'dark') {
      document.documentElement.classList.add('theme-transitioning');
      setThemeState(t);
      localStorage.setItem('app_theme', t);
      setTimeout(() => {
        document.documentElement.classList.remove('theme-transitioning');
      }, 300);
    }
  }, []);
 
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.classList.toggle('light', !isDark);
    document.body.classList.toggle('dark', isDark);
    document.body.classList.toggle('light', !isDark);
  }, [isDark]);
 
  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};